import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, CheckCircle2, FileText, Plus, RotateCcw, Save, XCircle } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import { useSession } from '@/core/auth/useSession';
import { AppError } from '@/core/errors/AppError';
import { ROUTES } from '@/core/config/routes';
import { NuevaReservaSchema, } from '@/data/dto/reserva';
import { useConfirmarReserva, useCrearReserva, useEditarReserva, useReserva, } from '@/data/hooks/useReservas';
import { usePagosDeReserva } from '@/data/hooks/usePagos';
import { useSucursales } from '@/data/hooks/useCatalogos';
import { reservasApi } from '@/data/api/reservasApi';
import { clasesApi } from '@/data/api/clasesApi';
import { Button } from '@/shared/components/Button';
import { Chip } from '@/shared/components/Chip';
import { Skeleton } from '@/shared/components/Skeleton';
import { StepUpAuthModal } from '@/shared/components/StepUpAuthModal';
import { useToast } from '@/shared/components/Toast';
import { CardContexto } from './components/CardContexto';
import { CardClienteSelector } from './components/CardClienteSelector';
import { CardAsignacionesEditor } from './components/CardAsignacionesEditor';
import { CardAuditoria } from './components/CardAuditoria';
import { CardSucursalBanner } from './components/CardSucursalBanner';
import { CardResumenEconomico } from './components/CardResumenEconomico';
import { CardPagos } from './components/CardPagos';
import { CardDescuentos } from './components/CardDescuentos';
import { BuscadorReservaHeader } from './components/BuscadorReservaHeader';
import { ClasesEditor, nuevoDraft } from './components/ClasesEditor';
import { RegistrarPagoModal } from './components/RegistrarPagoModal';
import { CancelarReservaModal } from './components/CancelarReservaModal';
const VALORES_DEFAULT = {
    clienteId: '',
    sucursalId: '',
    promotorId: '',
    empleadoSolicitadoId: '',
    metodoPagoPrevisto: 'EFECTIVO',
    observaciones: '',
};
export function ReservaFormPage() {
    const { id } = useParams();
    const modoEdicion = Boolean(id);
    const navigate = useNavigate();
    const toast = useToast();
    const { tieneRol, esPromotor } = useSession();
    const puedeEditar = tieneRol('ROLE_EDITAR_RESERVA');
    const puedeCancelarRole = tieneRol('ROLE_CANCELAR_RESERVA');
    const puedePagar = tieneRol('ROLE_REGISTRAR_PAGO');
    const puedeAdminDescuento = tieneRol('ROLE_ADMINISTRAR_DESCUENTO');
    // El promotor no aplica overrides de tarifa ni crea tarifas: el override
    // requiere step-up de admin y pasa por encima del precio congelado.
    const permiteOverrides = !esPromotor;
    // Tampoco elige instructor por clase: eso es información interna de la
    // empresa y queda a cargo del staff.
    const permiteElegirInstructor = !esPromotor;
    const form = useForm({
        resolver: zodResolver(NuevaReservaSchema),
        defaultValues: VALORES_DEFAULT,
    });
    const [drafts, setDrafts] = useState([]);
    const [pagoOpen, setPagoOpen] = useState(false);
    const [cancelarOpen, setCancelarOpen] = useState(false);
    const [stepUpOpen, setStepUpOpen] = useState(false);
    const { data: reserva, isLoading: cargandoReserva } = useReserva(id ?? null);
    const { data: pagos } = usePagosDeReserva(id ?? null);
    const { data: sucursales } = useSucursales();
    const crear = useCrearReserva();
    const editar = useEditarReserva();
    const confirmar = useConfirmarReserva();
    useEffect(() => {
        if (modoEdicion)
            return;
        if (sucursales?.length && !form.getValues('sucursalId')) {
            form.setValue('sucursalId', sucursales[0].id);
        }
    }, [sucursales, modoEdicion, form]);
    useEffect(() => {
        if (!modoEdicion || !reserva)
            return;
        // Esperamos a que `sucursales` haya cargado antes del reset: el
        // `<select>` de Sucursal está manejado por register() (uncontrolled),
        // así que si el reset corre antes de que existan las <option>, el
        // browser no asigna la opción correcta y queda "Seleccioná...".
        if (!sucursales?.length)
            return;
        form.reset({
            clienteId: reserva.clienteId,
            sucursalId: reserva.sucursalId,
            promotorId: reserva.promotorId ?? '',
            empleadoSolicitadoId: reserva.empleadoSolicitadoId ?? '',
            metodoPagoPrevisto: reserva.metodoPagoPrevisto,
            observaciones: reserva.observaciones ?? '',
        });
    }, [modoEdicion, reserva, sucursales, form]);
    const sucursalId = form.watch('sucursalId');
    const totalPagado = pagos?.totalPagado ?? 0;
    // Guarda los campos del encabezado de la reserva (cliente, sucursal,
    // promotor, instructor, observaciones). Se llama EXPLICITAMENTE desde el
    // boton "Guardar cambios" del banner sticky. Las acciones discretas
    // (Agregar clase, Cobrar, Cancelar) siguen siendo immediate-write.
    const guardarCambiosHeader = async () => {
        if (!modoEdicion || !reserva || !puedeEditar)
            return false;
        if (reserva.estado === 'CANCELADA' || reserva.estado === 'FINALIZADA')
            return false;
        const ok = await form.trigger();
        if (!ok) {
            toast.advertir('Revisá los campos resaltados antes de guardar.');
            return false;
        }
        const values = form.getValues();
        try {
            await editar.mutateAsync({ id: reserva.id, values });
            // Sincronizamos los "valores de referencia" del form para que isDirty
            // vuelva a false despues del guardado exitoso.
            form.reset(values);
            toast.exito('Cambios guardados');
            return true;
        }
        catch (err) {
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar.');
            return false;
        }
    };
    const descartarCambios = () => {
        if (!reserva)
            return;
        form.reset({
            clienteId: reserva.clienteId,
            sucursalId: reserva.sucursalId,
            promotorId: reserva.promotorId ?? '',
            empleadoSolicitadoId: reserva.empleadoSolicitadoId ?? '',
            metodoPagoPrevisto: reserva.metodoPagoPrevisto,
            observaciones: reserva.observaciones ?? '',
        });
    };
    // Si hay cambios sin guardar, bloqueamos la navegacion con un dialog que
    // pregunta "descartar o quedarse". El blocker se activa cuando isDirty es
    // true y solo durante la edicion.
    const isDirty = modoEdicion && form.formState.isDirty;
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (!isDirty)
            return false;
        return currentLocation.pathname !== nextLocation.pathname;
    });
    useEffect(() => {
        const handler = (e) => {
            if (!isDirty)
                return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);
    const hayOverrides = drafts.some((d) => d.precioOverride && d.precioOverride.trim() !== '');
    const persistirReservaConClases = async (stepUpToken) => {
        const values = form.getValues();
        const ok = await form.trigger();
        if (!ok)
            return;
        if (drafts.length === 0) {
            toast.advertir('Agregá al menos una clase antes de guardar.');
            return;
        }
        try {
            const nueva = await crear.mutateAsync(values);
            const requeridoId = values.empleadoSolicitadoId || undefined;
            for (const d of drafts) {
                if (!d.tarifaId || !d.meetingPointId || !d.fechaHoraInicio) {
                    toast.advertir('Completá todas las filas de clase.');
                    continue;
                }
                const inscripcion = await reservasApi.agregarClase(nueva.id, {
                    tarifaId: d.tarifaId,
                    meetingPointId: d.meetingPointId,
                    empleadoId: d.empleadoId || requeridoId,
                    fechaHoraInicio: new Date(d.fechaHoraInicio).toISOString(),
                    cantPersonas: d.tipoClase === 'GRUPAL' ? d.cantPersonas : undefined,
                });
                if (d.precioOverride && d.precioOverride.trim() !== '' && stepUpToken) {
                    try {
                        await clasesApi.aplicarPrecioOverride(inscripcion.claseId, {
                            monto: d.precioOverride,
                            motivo: d.precioOverrideMotivo || 'Precio manual al crear',
                            stepUpToken,
                        });
                    }
                    catch (err) {
                        toast.error(err instanceof AppError
                            ? `Override no aplicado: ${err.mensaje}`
                            : 'No se pudo aplicar un override de precio.');
                    }
                }
            }
            toast.exito('Reserva creada', `Código ${nueva.codigo}`);
            navigate(ROUTES.reservaDetalle(nueva.id), { replace: true });
        }
        catch (err) {
            if (err instanceof AppError && err.errores) {
                for (const e of err.errores) {
                    form.setError(e.campo, { message: e.mensaje });
                }
                return;
            }
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos crear la reserva.');
        }
    };
    const guardarTodo = form.handleSubmit(async () => {
        // En EDIT el form no debe crear reservas: cada sección persiste
        // con sus propias acciones. Guard para que cualquier submit
        // accidental (Enter en un input, botón mal tipado) no cree una
        // reserva fantasma.
        if (modoEdicion)
            return;
        if (hayOverrides) {
            // Pedí step-up una sola vez y aplicalo a todos los drafts con override.
            setStepUpOpen(true);
            return;
        }
        await persistirReservaConClases();
    }, 
    // Si la validación falla mostramos toast con los campos faltantes
    // así el operador ve por qué el botón "no hace nada".
    (errores) => {
        const faltantes = [];
        if (errores.clienteId)
            faltantes.push('cliente');
        if (errores.sucursalId)
            faltantes.push('sucursal');
        if (errores.metodoPagoPrevisto)
            faltantes.push('método de pago previsto');
        const lista = faltantes.length ? faltantes.join(', ') : 'los datos de la reserva';
        toast.advertir(`Completá ${lista} antes de guardar.`);
    });
    if (modoEdicion && cargandoReserva) {
        return (_jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(Skeleton, { className: "h-8 w-64" }), _jsxs("div", { className: "grid grid-cols-1 gap-3 lg:grid-cols-3", children: [_jsxs("div", { className: "flex flex-col gap-3 lg:col-span-2", children: [_jsx(Skeleton, { className: "h-32 w-full" }), _jsx(Skeleton, { className: "h-40 w-full" })] }), _jsxs("div", { className: "flex flex-col gap-3", children: [_jsx(Skeleton, { className: "h-40 w-full" }), _jsx(Skeleton, { className: "h-40 w-full" })] })] })] }));
    }
    const esEditable = !reserva || (reserva.estado !== 'CANCELADA' && reserva.estado !== 'FINALIZADA');
    const puedeConfirmar = reserva?.estado === 'SOLICITADA';
    const reservaDerivada = reserva ?? null;
    const saldo = reserva ? Math.max(0, (reserva.totalNeto ?? 0) - totalPagado) : 0;
    const pagosHabilitados = Boolean(reserva);
    return (_jsxs("form", { onSubmit: guardarTodo, className: "flex flex-col gap-3", noValidate: true, children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx(Button, { type: "button", variant: "ghost", size: "icon", onClick: () => navigate(ROUTES.reservas), "aria-label": "Volver", children: _jsx(ArrowLeft, { className: "h-4 w-4" }) }), _jsxs("div", { children: [_jsx("h1", { className: "flex items-center gap-2 text-xl font-bold text-text-primary", children: modoEdicion && reserva ? (_jsxs(_Fragment, { children: ["Reserva", _jsx("span", { className: "font-mono text-base text-primary", children: reserva.codigo }), reserva.canal === 'WEB' ? (_jsx(Chip, { tono: "info", children: "WEB" })) : (_jsx(Chip, { tono: "neutral", children: "GESTION" }))] })) : ('Nueva reserva') }), _jsx("p", { className: "text-xs text-text-secondary", children: modoEdicion
                                            ? 'Editá los datos y tocá "Guardar cambios" para confirmar.'
                                            : 'Completá los datos, agregá las clases y guardá en un solo paso.' })] })] }), _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(BuscadorReservaHeader, {}), modoEdicion ? (_jsxs(Button, { type: "button", variant: "secondary", size: "sm", onClick: () => navigate(ROUTES.reservaNueva), children: [_jsx(Plus, { className: "h-4 w-4" }), "Nueva en blanco"] })) : null, modoEdicion && puedeConfirmar && puedeEditar ? (_jsxs(Button, { type: "button", onClick: () => confirmar.mutate(reserva.id, {
                                    onSuccess: () => toast.exito('Reserva confirmada'),
                                    onError: (err) => toast.error(err instanceof AppError ? err.mensaje : 'No pudimos confirmar.'),
                                }), cargando: confirmar.isPending, size: "sm", children: [_jsx(CheckCircle2, { className: "h-4 w-4" }), "Confirmar"] })) : null, modoEdicion && puedeCancelarRole && esEditable ? (_jsxs(Button, { type: "button", variant: "secondary", size: "sm", onClick: () => setCancelarOpen(true), children: [_jsx(XCircle, { className: "h-4 w-4" }), "Cancelar"] })) : null, !modoEdicion ? (_jsxs(Button, { type: "submit", cargando: crear.isPending, children: [_jsx(Save, { className: "h-4 w-4" }), "Guardar reserva"] })) : null] })] }), isDirty ? (_jsxs("div", { className: "sticky top-2 z-sticky flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning bg-warning-fill px-4 py-2.5 text-sm shadow-card", children: [_jsxs("div", { className: "flex items-center gap-2 text-text-primary", children: [_jsx(FileText, { className: "h-4 w-4 text-warning" }), _jsx("span", { className: "font-medium", children: "Ten\u00E9s cambios sin guardar." })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: descartarCambios, disabled: editar.isPending, children: [_jsx(RotateCcw, { className: "h-3.5 w-3.5" }), "Descartar"] }), _jsxs(Button, { type: "button", size: "sm", onClick: () => void guardarCambiosHeader(), cargando: editar.isPending, children: [_jsx(Save, { className: "h-3.5 w-3.5" }), "Guardar cambios"] })] })] })) : null, _jsxs("div", { className: "grid grid-cols-1 gap-3 lg:grid-cols-3", children: [_jsxs("div", { className: "flex flex-col gap-3 lg:col-span-2", children: [_jsx(CardSucursalBanner, { form: form, disabled: modoEdicion && !esEditable, onBlurPersistir: undefined }), _jsx(ClasesEditor, { sucursalId: sucursalId, inscripciones: reserva?.inscripciones ?? [], drafts: drafts, onDraftsChange: setDrafts, reservaIdActiva: modoEdicion ? reserva?.id : undefined, empleadoRequeridoId: form.watch('empleadoSolicitadoId') || undefined, permiteOverrides: permiteOverrides, permiteElegirInstructor: permiteElegirInstructor }), _jsxs("div", { className: "grid grid-cols-1 gap-3 md:grid-cols-2", children: [_jsx(CardAsignacionesEditor, { form: form, disabled: modoEdicion && !esEditable, onBlurPersistir: undefined, reserva: reservaDerivada }), _jsx(CardClienteSelector, { form: form, readOnlyMode: modoEdicion, onBlurPersistir: undefined })] }), _jsx(CardContexto, { form: form, disabled: modoEdicion, onBlurPersistir: undefined, codigo: reserva?.codigo, estado: reserva?.estado, creadoEn: reserva?.creadoEn, creadoPor: reserva?.creadoPor ?? null })] }), _jsxs("div", { className: "flex flex-col gap-3 lg:sticky lg:top-3 lg:self-start", children: [_jsx(CardResumenEconomico, { reserva: reservaDerivada, totalPagado: totalPagado, cantDrafts: drafts.length }), _jsx(CardPagos, { reservaId: reserva?.id ?? null, onRegistrarPago: () => setPagoOpen(true), puedePagar: puedePagar && esEditable, habilitado: pagosHabilitados }), modoEdicion && reserva ? (_jsx(CardDescuentos, { reservaId: reserva.id, puedeEditar: puedeAdminDescuento && esEditable })) : null, modoEdicion && reserva ? _jsx(CardAuditoria, { reserva: reserva }) : null] })] }), modoEdicion && reserva ? (_jsxs(_Fragment, { children: [_jsx(RegistrarPagoModal, { open: pagoOpen, onOpenChange: setPagoOpen, reservaId: reserva.id, sucursalId: reserva.sucursalId, saldoPendiente: saldo, metodoSugerido: reserva.metodoPagoPrevisto }), _jsx(CancelarReservaModal, { open: cancelarOpen, onOpenChange: setCancelarOpen, reservaId: reserva.id, codigo: reserva.codigo })] })) : null, _jsx(StepUpAuthModal, { open: stepUpOpen, onOpenChange: setStepUpOpen, proposito: "override_precio_clase", titulo: "Autorizar precios manuales", descripcion: "Hay clases con precio manual en esta reserva. Ingres\u00E1 tu contrase\u00F1a para autorizarlas.", pedirMotivo: false, onConfirmar: async ({ token }) => {
                    await persistirReservaConClases(token);
                } }), _jsx(Dialog.Root, { open: blocker.state === 'blocked', onOpenChange: (o) => { if (!o)
                    blocker.reset?.(); }, children: _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "fixed inset-0 z-modal bg-black/40" }), _jsxs(Dialog.Content, { className: "fixed left-1/2 top-1/2 z-modal w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-surface p-5 shadow-modal dark:shadow-modal-dark", children: [_jsx(Dialog.Title, { className: "text-lg font-bold text-text-primary", children: "Ten\u00E9s cambios sin guardar" }), _jsx(Dialog.Description, { className: "mt-2 text-sm text-text-secondary", children: "Si sal\u00EDs ahora se descartan. \u00BFQuer\u00E9s guardar antes o continuar sin guardar?" }), _jsxs("div", { className: "mt-4 flex flex-col-reverse gap-2 md:flex-row md:justify-end", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: () => blocker.reset?.(), children: "Volver al formulario" }), _jsx(Button, { type: "button", variant: "secondary", onClick: () => {
                                                descartarCambios();
                                                blocker.proceed?.();
                                            }, children: "Descartar y salir" }), _jsxs(Button, { type: "button", onClick: async () => {
                                                const ok = await guardarCambiosHeader();
                                                if (ok)
                                                    blocker.proceed?.();
                                                else
                                                    blocker.reset?.();
                                            }, cargando: editar.isPending, children: [_jsx(Save, { className: "h-4 w-4" }), "Guardar y salir"] })] })] })] }) })] }));
}
export { nuevoDraft };
